import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Globe,
    BookOpen,
    FileText,
    Instagram,
    MessageSquare,
    Award,
    Calendar,
    ArrowUpRight,
    Sparkles,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";

// Translations
const translations = {
    fa: {
        meta: {
            title: "دلبند | بیولینک — همهٔ مسیرها به مراقبت هوشمند",
            description: "بیولینک دلبند: دسترسی سریع به اپ، راهنمای نصب، پشتیبانی، فروشگاه و شبکه‌های اجتماعی. همهٔ خدمات مراقبت هوشمند یک‌جا.",
            keywords: "دلبند, بیولینک, Delband, لینک‌ها, اپ دلبند, نصب مچ‌بند, راهنمای کاربر, پشتیبانی, سلامت هوشمند",
            ogTitle: "دلبند — بیولینک | دسترسی سریع به خدمات و پشتیبانی",
            ogDescription: "دسترسی به اپ، راهنمای نصب پوشیدنی، پشتیبانی و خرید؛ همه در یک بیولینک سریع از دلبند.",
        },
        logoAlt: "لوگوی دلبند",
        badge: "پایش لحظه ای سلامت با پوشیدنی های هوشمند",
        subtitle: "اولین پلتفرم نسل سوم پزشکی در ایران",
        partnership: "با همکاری و حمایت راهبردی",
        partnerName: "همراه اول",
        shareBtn: "اشتراک‌گذاری",
        copiedToast: "لینک بیولینک کپی شد!",
        backHome: "صفحه اصلی وب‌سایت",
        links: {
            website: {
                title: "بازدید از وب‌سایت اصلی دلبند",
                subtitle: "آشنایی با محصولات، اپلیکیشن و امکانات سلامت",
                tag: "سایت رسمی",
            },
            catalog: {
                title: "دریافت کاتالوگ جامع محصولات",
                subtitle: "مشاهده مشخصات فنی و قابلیت‌های پایشگرها",
                tag: "کاتالوگ PDF",
            },
            form: {
                title: "فرم ویژه سازمان‌ها و مراکز درمانی",
                subtitle: "ثبت درخواست همکاری سازمانی و تجهیز پرسنل",
                tag: "B2B و سازمانی",
            },
            instagram: {
                title: "صفحه رسمی اینستاگرام",
                subtitle: "آموزش‌های سلامت، اخبار و رویدادهای دلبند",
                tag: "@delband.care",
            },
            bale: {
                title: "ارتباط و پشتیبانی در بله",
                subtitle: "گفتگوی مستقیم با کارشناسان و تیم پشتیبانی",
                tag: "پشتیبانی آنلاین",
            },
        },
        mediaTitle: "دلبند در رسانه‌ها و افتخارات",
        mediaHighlights: {
            elecomp1404: {
                title: "گزارش صدا و سیما از دلبند در الکامپ",
                date: "ارائه دستیار هوش مصنوعی سلامت و پنل سازمانی سلامت دیجیتال",
                tag: "گزارش خبری",
            },
            award: {
                title: "تندیس و جایزه نوآوری سلامت دیجیتال",
                date: "برگزیده ششمین کنگره بین‌المللی سلامت همراه (mHealth)",
                tag: "افتخار ملی",
            },
            telecom1403: {
                title: "گزارش اختصاصی زومیت از دلبند در تلکام",
                date: "بررسی راهکارهای پوشیدنی سلامت هوشمند و تحلیل داده‌های حیاتی",
                tag: "رسانه فناوری",
            },
        },
        viewButton: "مشاهده",
    },
    en: {
        meta: {
            title: "Delband | Biolink — All Paths to Smart Care",
            description: "Delband Biolink: Quick access to app, installation guide, support, store and social networks. All smart care services in one place.",
            keywords: "Delband, biolink, links, Delband app, wearable installation, user guide, support, smart health",
            ogTitle: "Delband — Biolink | Quick Access to Services & Support",
            ogDescription: "Access to app, wearable installation guide, support and store; all in one quick biolink from Delband.",
        },
        logoAlt: "Delband Logo",
        badge: "Gen-3 Digital Health Platform",
        subtitle: "The First Medical Digital Health Monitoring Platform in Iran",
        partnership: "Supported and Partnered by",
        partnerName: "MCI (Hamrah-e-Avval)",
        shareBtn: "Share",
        copiedToast: "Link copied to clipboard!",
        backHome: "Website Home",
        links: {
            website: {
                title: "Visit Delband Official Website",
                subtitle: "Explore products, mobile app & health monitoring features",
                tag: "Official Website",
            },
            catalog: {
                title: "Download Comprehensive Catalog",
                subtitle: "Technical specifications and clinical wearable capabilities",
                tag: "PDF Catalog",
            },
            form: {
                title: "Organizations & Clinics Form",
                subtitle: "Submit enterprise cooperation & corporate wellness requests",
                tag: "B2B & Enterprise",
            },
            instagram: {
                title: "Official Instagram Channel",
                subtitle: "Health tips, updates and product announcements",
                tag: "@delband.care",
            },
            bale: {
                title: "Direct Support on Bale",
                subtitle: "Chat with customer success and medical engineering specialists",
                tag: "Live Support",
            },
        },
        mediaTitle: "Delband in Media & Honors",
        mediaHighlights: {
            elecomp1404: {
                title: "National TV Report on Delband at Elecomp",
                date: "Presenting AI Health Assistant & Corporate Wellness Dashboard",
                tag: "News Broadcast",
            },
            award: {
                title: "Digital Health Innovation Award",
                date: "Selected at the 6th International mHealth Congress",
                tag: "National Honor",
            },
            telecom1403: {
                title: "Zoomit Exclusive Feature at Telecom",
                date: "In-depth review of Iranian smart clinical health monitoring solutions",
                tag: "Tech Media",
            },
        },
        viewButton: "View",
    },
};

const BioLinkPage: React.FC = () => {
    const { i18n } = useTranslation();
    const currentLang = (i18n.language as keyof typeof translations) || "fa";
    const t = translations[currentLang] || translations.fa;
    const isRTL = currentLang === "fa";

    const [hoveredCard, setHoveredCard] = useState<string | null>(null);
    const mainCardRef = useRef<HTMLDivElement>(null);

    // SEO Meta Tags
    useEffect(() => {
        const ensure = (selector: string, tag: string, attrs: Record<string, string> = {}) => {
            let el = document.head.querySelector(selector);
            if (!el) {
                el = document.createElement(tag);
                Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
                document.head.appendChild(el);
            }
            return el;
        };

        const prevTitle = document.title;
        document.title = t.meta.title;

        const metaDescription = ensure('meta[name="description"]', "meta", { name: "description" });
        metaDescription.setAttribute("content", t.meta.description);

        const metaKeywords = ensure('meta[name="keywords"]', "meta", { name: "keywords" });
        metaKeywords.setAttribute("content", t.meta.keywords);

        const linkCanonical = ensure('link[rel="canonical"]', "link", { rel: "canonical" });
        linkCanonical.setAttribute("href", "https://delband.care/biolink");

        const ogTitle = ensure('meta[property="og:title"]', "meta", { property: "og:title" });
        ogTitle.setAttribute("content", t.meta.ogTitle);

        const ogDesc = ensure('meta[property="og:description"]', "meta", { property: "og:description" });
        ogDesc.setAttribute("content", t.meta.ogDescription);

        const ogImage = ensure('meta[property="og:image"]', "meta", { property: "og:image" });
        ogImage.setAttribute("content", "https://delband.care/assets/biolink-og.jpg");

        const ogUrl = ensure('meta[property="og:url"]', "meta", { property: "og:url" });
        ogUrl.setAttribute("content", "https://delband.care/biolink");

        ensure('meta[property="og:type"]', "meta", { property: "og:type" }).setAttribute("content", "website");

        return () => {
            document.title = prevTitle || "Delband";
        };
    }, [t, currentLang]);

    // Parallax Tilt on 3D Container for desktop
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!mainCardRef.current || window.innerWidth < 768) return;
            const { clientX, clientY } = e;
            const { left, top, width, height } = mainCardRef.current.getBoundingClientRect();
            const x = (clientX - left) / width - 0.5;
            const y = (clientY - top) / height - 0.5;
            mainCardRef.current.style.transform = `perspective(1200px) rotateY(${x * 4}deg) rotateX(${-y * 4}deg) translateY(-2px)`;
        };

        const handleMouseLeave = () => {
            if (!mainCardRef.current) return;
            mainCardRef.current.style.transform = "perspective(1200px) rotateY(0deg) rotateX(0deg) translateY(0px)";
        };

        const card = mainCardRef.current;
        if (card) {
            window.addEventListener("mousemove", handleMouseMove);
            card.addEventListener("mouseleave", handleMouseLeave);
        }

        return () => {
            if (card) {
                window.removeEventListener("mousemove", handleMouseMove);
                card.removeEventListener("mouseleave", handleMouseLeave);
            }
        };
    }, []);

    const links = [
        {
            id: "website",
            title: t.links.website.title,
            subtitle: t.links.website.subtitle,
            icon: <Globe className="w-5 h-5" />,
            url: "/",
            internal: true,
            themeColor: "blue",
            accentBg: "bg-blue-600",
            iconBg: "bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white group-active:bg-blue-600 group-active:text-white",
            borderColor: "hover:border-blue-300 active:border-blue-400",
            glowColor: "hover:shadow-blue-500/15",
            shimmerColor: "from-blue-500/0 via-blue-500/10 to-blue-500/0",
        },
        {
            id: "catalog",
            title: t.links.catalog.title,
            subtitle: t.links.catalog.subtitle,
            icon: <BookOpen className="w-5 h-5" />,
            url: "https://catalog.delbandcare.com/",
            internal: false,
            themeColor: "indigo",
            accentBg: "bg-indigo-600",
            iconBg: "bg-indigo-50 text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white group-active:bg-indigo-600 group-active:text-white",
            borderColor: "hover:border-indigo-300 active:border-indigo-400",
            glowColor: "hover:shadow-indigo-500/15",
            shimmerColor: "from-indigo-500/0 via-indigo-500/10 to-indigo-500/0",
        },
        {
            id: "form",
            title: t.links.form.title,
            subtitle: t.links.form.subtitle,
            icon: <FileText className="w-5 h-5" />,
            url: "https://survey.porsline.ir/s/gJ1ZAfwb",
            internal: false,
            themeColor: "sky",
            accentBg: "bg-sky-600",
            iconBg: "bg-sky-50 text-sky-600 group-hover:bg-sky-600 group-hover:text-white group-active:bg-sky-600 group-active:text-white",
            borderColor: "hover:border-sky-300 active:border-sky-400",
            glowColor: "hover:shadow-sky-500/15",
            shimmerColor: "from-sky-500/0 via-sky-500/10 to-sky-500/0",
        },
        {
            id: "instagram",
            title: t.links.instagram.title,
            subtitle: t.links.instagram.subtitle,
            icon: <Instagram className="w-5 h-5" />,
            url: "https://www.instagram.com/delband.care",
            internal: false,
            themeColor: "blue",
            accentBg: "bg-gradient-to-tr from-blue-600 to-indigo-600",
            iconBg: "bg-blue-50 text-blue-600 group-hover:bg-gradient-to-tr group-hover:from-blue-600 group-hover:to-indigo-600 group-hover:text-white group-active:bg-blue-600 group-active:text-white",
            borderColor: "hover:border-blue-300 active:border-blue-400",
            glowColor: "hover:shadow-blue-500/15",
            shimmerColor: "from-pink-500/0 via-indigo-500/10 to-blue-500/0",
        },
        {
            id: "bale",
            title: t.links.bale.title,
            subtitle: t.links.bale.subtitle,
            icon: <MessageSquare className="w-5 h-5" />,
            url: "https://ble.ir/delband_care",
            internal: false,
            themeColor: "cyan",
            accentBg: "bg-cyan-600",
            iconBg: "bg-cyan-50 text-cyan-600 group-hover:bg-cyan-600 group-hover:text-white group-active:bg-cyan-600 group-active:text-white",
            borderColor: "hover:border-cyan-300 active:border-cyan-400",
            glowColor: "hover:shadow-cyan-500/15",
            shimmerColor: "from-cyan-500/0 via-cyan-500/10 to-cyan-500/0",
        },
    ];

    const mediaHighlights = [
        {
            id: "elecomp1404",
            title: t.mediaHighlights.elecomp1404.title,
            date: t.mediaHighlights.elecomp1404.date,
            icon: <Calendar className="w-4 h-4" />,
            url: "https://telewebion.com/episode/0x15205fd4",
        },
        {
            id: "award",
            title: t.mediaHighlights.award.title,
            date: t.mediaHighlights.award.date,
            icon: <Award className="w-4 h-4" />,
            url: "https://business.mci.ir/news/-/view/3162901",
        },
        {
            id: "telecom1403",
            title: t.mediaHighlights.telecom1403.title,
            date: t.mediaHighlights.telecom1403.date,
            icon: <Calendar className="w-4 h-4" />,
            url: "https://www.aparat.com/v/repk4pb?t=194",
        },
    ];

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.08, delayChildren: 0.12 },
        },
    };

    const itemVariants = {
        hidden: { y: 22, opacity: 0, scale: 0.97 },
        visible: {
            y: 0,
            opacity: 1,
            scale: 1,
            transition: { type: "spring" as const, stiffness: 380, damping: 24 },
        },
    };

    return (
        <div
            className="min-h-screen min-h-[100dvh] bg-gradient-to-br from-sky-100/80 via-blue-50/60 via-indigo-100/50 to-blue-200/70 relative flex flex-col items-center justify-center p-3.5 sm:p-6 md:py-12 overflow-x-hidden selection:bg-blue-500 selection:text-white"
            dir={isRTL ? "rtl" : "ltr"}
        >
            {/* Ambient Background Animated Gradient Glows & Dynamic Mesh (Light Theme) */}
            <div className="fixed inset-0 pointer-events-none -z-10 overflow-hidden">
                {/* Large Morphing Gradient Blob 1 - Top Left */}
                <motion.div
                    animate={{
                        scale: [1, 1.28, 0.92, 1.18, 1],
                        x: [0, 45, -35, 20, 0],
                        y: [0, -40, 25, -15, 0],
                        rotate: [0, 90, 180, 270, 360],
                    }}
                    transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute -top-20 -left-28 w-[420px] sm:w-[580px] h-[420px] sm:h-[580px] bg-gradient-to-br from-blue-400/40 via-sky-300/35 to-indigo-200/20 rounded-full blur-3xl"
                />

                {/* Large Morphing Gradient Blob 2 - Center Right */}
                <motion.div
                    animate={{
                        scale: [1, 1.22, 0.88, 1.15, 1],
                        x: [0, -50, 30, -20, 0],
                        y: [0, 35, -45, 20, 0],
                        rotate: [360, 270, 180, 90, 0],
                    }}
                    transition={{ duration: 26, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                    className="absolute top-1/4 -right-32 w-[440px] sm:w-[620px] h-[440px] sm:h-[620px] bg-gradient-to-bl from-sky-400/35 via-cyan-200/30 to-blue-300/25 rounded-full blur-3xl"
                />

                {/* Large Morphing Gradient Blob 3 - Bottom Left */}
                <motion.div
                    animate={{
                        scale: [1, 1.18, 0.95, 1],
                        x: [0, 30, -25, 0],
                        y: [0, -30, 35, 0],
                    }}
                    transition={{ duration: 20, repeat: Infinity, ease: "easeInOut", delay: 2.5 }}
                    className="absolute -bottom-32 left-1/6 w-[480px] sm:w-[680px] h-[480px] sm:h-[680px] bg-gradient-to-tr from-indigo-300/35 via-blue-200/35 to-sky-100/20 rounded-full blur-3xl"
                />

                {/* Floating 3D-Style Glass Gradient Spheres */}
                <motion.div
                    animate={{
                        y: [0, -32, 0],
                        x: [0, 16, 0],
                        rotate: [0, 180, 360],
                    }}
                    transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute top-[18%] left-[8%] sm:left-[15%] w-24 sm:w-32 h-24 sm:h-32 rounded-full bg-gradient-to-br from-white/90 via-sky-200/50 to-blue-400/30 backdrop-blur-md border border-white/80 shadow-[0_8px_32px_rgba(56,189,248,0.25)]"
                >
                    <div className="absolute top-3 left-4 w-6 h-4 rounded-full bg-white/70 blur-[2px]" />
                </motion.div>

                <motion.div
                    animate={{
                        y: [0, 28, 0],
                        x: [0, -18, 0],
                        rotate: [360, 180, 0],
                    }}
                    transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
                    className="absolute bottom-[20%] right-[6%] sm:right-[14%] w-28 sm:w-36 h-28 sm:h-36 rounded-full bg-gradient-to-br from-white/85 via-indigo-200/45 to-sky-400/25 backdrop-blur-md border border-white/80 shadow-[0_8px_32px_rgba(99,102,241,0.2)]"
                >
                    <div className="absolute top-4 left-5 w-7 h-4 rounded-full bg-white/70 blur-[2px]" />
                </motion.div>

                <motion.div
                    animate={{
                        y: [0, -20, 0],
                        scale: [1, 1.12, 1],
                    }}
                    transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
                    className="absolute top-[55%] left-[4%] sm:left-[10%] w-16 sm:w-20 h-16 sm:h-20 rounded-full bg-gradient-to-br from-white/80 via-blue-200/40 to-cyan-300/30 backdrop-blur-sm border border-white/70 shadow-lg shadow-blue-400/15"
                >
                    <div className="absolute top-2 left-2.5 w-4 h-2.5 rounded-full bg-white/60 blur-[1px]" />
                </motion.div>

                <motion.div
                    animate={{
                        y: [0, 22, 0],
                        scale: [1, 1.15, 1],
                    }}
                    transition={{ duration: 9, repeat: Infinity, ease: "easeInOut", delay: 2.2 }}
                    className="absolute top-[12%] right-[10%] sm:right-[18%] w-14 sm:w-18 h-14 sm:h-18 rounded-full bg-gradient-to-br from-white/80 via-cyan-200/40 to-indigo-300/30 backdrop-blur-sm border border-white/70 shadow-lg shadow-sky-400/15"
                >
                    <div className="absolute top-2 left-2 w-3.5 h-2 rounded-full bg-white/60 blur-[1px]" />
                </motion.div>
            </div>

            {/* Background Medical Tech Grid & Radar Rings */}
            <div className="fixed inset-0 pointer-events-none -z-10 opacity-70 overflow-hidden">
                {/* Dot Grid Pattern */}
                <div
                    className="absolute inset-0"
                    style={{
                        backgroundImage: "radial-gradient(rgba(37, 99, 235, 0.18) 1.2px, transparent 1.2px)",
                        backgroundSize: "22px 22px",
                    }}
                />

                {/* Subtle Concentric Radar Rings & Expanding Pulses */}
                <div className="absolute top-1/4 -left-28 w-[520px] h-[520px] rounded-full border border-blue-400/20 pointer-events-none" />
                <motion.div
                    animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0.6, 0.3] }}
                    transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute top-1/4 -left-20 w-[460px] h-[460px] rounded-full border border-blue-400/25 pointer-events-none"
                />
                <div className="absolute top-1/4 -left-16 w-[420px] h-[420px] rounded-full border border-dashed border-blue-500/30 pointer-events-none animate-[spin_50s_linear_infinite]" />
                <div className="absolute top-1/4 -left-6 w-[320px] h-[320px] rounded-full border border-blue-400/20 pointer-events-none" />

                {/* Bottom Right Concentric Rings */}
                <div className="absolute -bottom-36 -right-36 w-[520px] h-[520px] rounded-full border border-indigo-400/20 pointer-events-none" />
                <motion.div
                    animate={{ scale: [1, 1.25, 1], opacity: [0.25, 0.55, 0.25] }}
                    transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                    className="absolute -bottom-28 -right-28 w-[450px] h-[450px] rounded-full border border-indigo-400/25 pointer-events-none"
                />
                <div className="absolute -bottom-20 -right-20 w-[400px] h-[400px] rounded-full border border-dashed border-indigo-500/30 pointer-events-none animate-[spin_40s_linear_infinite_reverse]" />

                {/* Ambient Dual ECG Pulse Lines */}
                <div className="absolute top-10 left-0 right-0 h-24 opacity-40 overflow-hidden">
                    <svg className="w-full h-full text-blue-500" preserveAspectRatio="none" viewBox="0 0 1000 60" fill="none">
                        <path
                            d="M 0 30 L 300 30 L 320 15 L 335 48 L 350 8 L 365 52 L 380 30 L 650 30 L 670 15 L 685 48 L 700 8 L 715 52 L 730 30 L 1000 30"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            className="animate-[dash_6s_linear_infinite]"
                            strokeDasharray="1000"
                        />
                    </svg>
                </div>
                <div className="absolute bottom-16 left-0 right-0 h-20 opacity-25 overflow-hidden">
                    <svg className="w-full h-full text-sky-400" preserveAspectRatio="none" viewBox="0 0 1000 60" fill="none">
                        <path
                            d="M 0 30 L 200 30 L 220 18 L 235 44 L 250 12 L 265 48 L 280 30 L 750 30 L 770 18 L 785 44 L 800 12 L 815 48 L 830 30 L 1000 30"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            className="animate-[dash_8s_linear_infinite]"
                            strokeDasharray="1000"
                        />
                    </svg>
                </div>
            </div>

            {/* Subtle Animated Floating Geometric / Vital Elements */}
            <div className="fixed inset-0 pointer-events-none -z-10 overflow-hidden">
                {/* Floating Plus / Cross Elements */}
                {[
                    { top: "12%", left: "8%", delay: 0 },
                    { top: "32%", right: "10%", delay: 1.2 },
                    { top: "65%", left: "7%", delay: 2.1 },
                    { top: "85%", right: "12%", delay: 0.8 },
                ].map((pos, i) => (
                    <motion.div
                        key={`cross-${i}`}
                        className="absolute text-blue-500/40 text-lg font-light select-none"
                        style={{ top: pos.top, left: (pos as any).left, right: (pos as any).right }}
                        animate={{
                            y: [0, -22, 0],
                            rotate: [0, 90, 180],
                            opacity: [0.3, 0.75, 0.3],
                        }}
                        transition={{
                            duration: 5 + i,
                            repeat: Infinity,
                            delay: pos.delay,
                            ease: "easeInOut",
                        }}
                    >
                        +
                    </motion.div>
                ))}

                {/* Floating Glowing Particles */}
                {[...Array(18)].map((_, i) => (
                    <motion.div
                        key={`dot-${i}`}
                        className="absolute w-2.5 h-2.5 rounded-full bg-gradient-to-tr from-blue-400 via-sky-300 to-indigo-300 shadow-[0_0_8px_rgba(56,189,248,0.5)] blur-[0.5px]"
                        style={{
                            left: `${(i * 13 + 5) % 100}%`,
                            top: `${(i * 15 + 7) % 100}%`,
                        }}
                        animate={{
                            y: [0, -32, 0],
                            opacity: [0.25, 0.75, 0.25],
                            scale: [1, 1.5, 1],
                        }}
                        transition={{
                            duration: 3.5 + (i % 4),
                            repeat: Infinity,
                            delay: i * 0.25,
                            ease: "easeInOut",
                        }}
                    />
                ))}
            </div>

            {/* Main BioLink Card Container */}
            <main className="w-full max-w-lg relative px-0.5 sm:px-0 my-auto py-2 sm:py-0">
                <motion.div
                    ref={mainCardRef}
                    initial={{ opacity: 0, y: 24, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                    className="relative backdrop-blur-2xl bg-white/65 sm:bg-white/60 border border-white/80 rounded-[1.75rem] sm:rounded-3xl shadow-[0_20px_60px_-15px_rgba(37,99,235,0.22),0_8px_32px_-6px_rgba(56,189,248,0.15)] overflow-hidden transition-transform duration-200 will-change-transform"
                >
                    {/* Animated Moving Gradient Orbs & Aurora Mesh INSIDE the Card */}
                    <div className="absolute inset-0 pointer-events-none -z-10 overflow-hidden">
                        {/* Dynamic Floating Gradient Orb 1 - Top Center/Right */}
                        <motion.div
                            animate={{
                                x: [0, 35, -25, 0],
                                y: [0, -30, 20, 0],
                                scale: [1, 1.3, 0.9, 1],
                                rotate: [0, 120, 240, 360],
                            }}
                            transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
                            className="absolute -top-16 -right-16 w-64 h-64 bg-gradient-to-bl from-blue-400/35 via-sky-300/30 to-transparent rounded-full blur-2xl"
                        />

                        {/* Dynamic Floating Gradient Orb 2 - Top Left */}
                        <motion.div
                            animate={{
                                x: [0, -30, 20, 0],
                                y: [0, 25, -25, 0],
                                scale: [1, 1.25, 0.95, 1],
                                rotate: [360, 240, 120, 0],
                            }}
                            transition={{ duration: 16, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                            className="absolute -top-12 -left-12 w-60 h-60 bg-gradient-to-br from-indigo-300/30 via-sky-200/35 to-transparent rounded-full blur-2xl"
                        />

                        {/* Dynamic Floating Gradient Orb 3 - Center Aurora */}
                        <motion.div
                            animate={{
                                scale: [1, 1.35, 1],
                                opacity: [0.35, 0.6, 0.35],
                                y: [-15, 20, -15],
                            }}
                            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
                            className="absolute top-1/3 left-1/2 -translate-x-1/2 w-72 h-72 bg-gradient-to-tr from-cyan-200/30 via-blue-200/25 to-indigo-200/25 rounded-full blur-3xl"
                        />

                        {/* Dynamic Floating Gradient Orb 4 - Bottom Center */}
                        <motion.div
                            animate={{
                                x: [0, 25, -30, 0],
                                y: [0, -20, 25, 0],
                                scale: [1, 1.2, 1],
                            }}
                            transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: 2 }}
                            className="absolute -bottom-20 left-1/4 w-72 h-72 bg-gradient-to-tr from-blue-300/30 via-indigo-200/30 to-sky-200/25 rounded-full blur-2xl"
                        />

                        {/* Glowing Animated Gradient Beams & Shimmer inside the card */}
                        <motion.div
                            animate={{
                                opacity: [0.15, 0.4, 0.15],
                                rotate: [0, 180, 360],
                            }}
                            transition={{ duration: 24, repeat: Infinity, ease: "linear" }}
                            className="absolute -inset-10 bg-[conic-gradient(from_0deg,transparent_0_300deg,rgba(56,189,248,0.2)_360deg)] blur-xl"
                        />

                        {/* Flowing Subtle Grid inside Card */}
                        <div
                            className="absolute inset-0 opacity-25"
                            style={{
                                backgroundImage: "radial-gradient(rgba(37, 99, 235, 0.15) 1px, transparent 1px)",
                                backgroundSize: "18px 18px",
                            }}
                        />
                    </div>

                    {/* Faint Medical Wave Line in Card Background */}
                    <div className="absolute top-20 left-0 right-0 h-28 opacity-[0.14] pointer-events-none -z-10 overflow-hidden">
                        <svg className="w-full h-full text-blue-600" viewBox="0 0 400 80" fill="none">
                            <path
                                d="M 0 40 L 120 40 L 140 20 L 155 65 L 170 10 L 185 70 L 200 40 L 400 40"
                                stroke="currentColor"
                                strokeWidth="2.5"
                            />
                        </svg>
                    </div>

                    {/* Top Decorative Blue Accent Line */}
                    <div className="h-1.5 w-full bg-gradient-to-r from-blue-600 via-sky-500 to-indigo-600" />

                    {/* Profile & Header Section */}
                    <div className="pt-6 sm:pt-8 pb-5 sm:pb-6 px-4 sm:px-6 text-center relative">
                        {/* Interactive Logo Avatar */}
                        <div className="relative mx-auto w-20 h-20 sm:w-24 sm:h-24 mb-3.5 sm:mb-4 flex items-center justify-center group cursor-pointer">
                            {/* Outer Pulsing Aura */}
                            <motion.div
                                className="absolute inset-0 rounded-full bg-gradient-to-tr from-blue-400/40 via-sky-300/40 to-indigo-400/30 blur-lg -z-10"
                                animate={{ scale: [1, 1.25, 1], opacity: [0.45, 0.85, 0.45] }}
                                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                            />
                            
                            {/* Rotating Gradient Ring on hover/touch */}
                            <div className="absolute -inset-1.5 rounded-full border-2 border-dashed border-blue-400/50 group-hover:border-blue-500/80 group-hover:animate-[spin_8s_linear_infinite] transition-all duration-300" />

                            {/* White Glass Logo Container */}
                            <motion.div
                                whileHover={{ scale: 1.06, rotate: [0, -2, 2, 0] }}
                                whileTap={{ scale: 0.94 }}
                                transition={{
                                    scale: { type: "spring", stiffness: 400, damping: 15 },
                                    rotate: { duration: 0.35, ease: "easeInOut" },
                                }}
                                className="relative w-full h-full bg-white/90 backdrop-blur-md rounded-full p-3.5 sm:p-4 border border-white shadow-md shadow-blue-500/20 flex items-center justify-center overflow-hidden"
                            >
                                <img
                                    src="/media/Logo1.svg"
                                    alt={t.logoAlt}
                                    className="w-12 h-12 sm:w-14 sm:h-14 object-contain filter drop-shadow-[0_2px_8px_rgba(37,99,235,0.3)] transition-transform duration-300 group-hover:scale-105"
                                />
                            </motion.div>
                        </div>

                        {/* Brand Title */}
                        <motion.h1
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="text-xl sm:text-2xl font-black bg-gradient-to-r from-blue-700 via-blue-600 to-sky-600 bg-clip-text text-transparent tracking-tight"
                        >
                            دلبند | Delband
                        </motion.h1>

                        {/* Tagline / Subtitle */}
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.15 }}
                            className="text-blue-700/90 text-xs sm:text-sm font-semibold mt-1 mb-1"
                        >
                            {t.badge}
                        </motion.p>

                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.2 }}
                            className="text-slate-600 text-[11px] sm:text-xs max-w-sm mx-auto leading-relaxed"
                        >
                            {t.subtitle}
                        </motion.p>

                        {/* Partnership with Hamrah Avval */}
                        <motion.div
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.25 }}
                            className="mt-3.5 inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-xl bg-white/70 backdrop-blur-md border border-white/90 shadow-2xs hover:border-blue-300 hover:bg-white/90 transition-all duration-200"
                        >
                            <span className="text-[11px] text-slate-500 font-medium">{t.partnership}</span>
                            <div className="h-3.5 w-px bg-slate-300" />
                            <img
                                src="/media/HamrahAvval.svg"
                                alt="همراه اول"
                                className="h-3.5 sm:h-4 w-auto object-contain"
                            />
                        </motion.div>
                    </div>

                    {/* Links Section */}
                    <div className="p-3.5 sm:p-5 pt-0">
                        <motion.div
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                            className="space-y-2.5 sm:space-y-3"
                        >
                            {links.map((link) => {
                                const isHovered = hoveredCard === link.id;

                                const CardContent = (
                                    <motion.div
                                        onMouseEnter={() => setHoveredCard(link.id)}
                                        onMouseLeave={() => setHoveredCard(null)}
                                        whileHover={{ y: -3, scale: 1.015 }}
                                        whileTap={{ scale: 0.96, y: 0 }}
                                        transition={{ type: "spring", stiffness: 420, damping: 24 }}
                                        className={`group relative w-full p-3.5 sm:p-3.5 rounded-[1.25rem] sm:rounded-2xl bg-white/75 hover:bg-white/95 active:bg-blue-50/80 backdrop-blur-md border border-white/90 ${link.borderColor} shadow-xs ${link.glowColor} hover:shadow-lg transition-all duration-200 flex items-center justify-between cursor-pointer overflow-hidden min-h-[64px] sm:min-h-[58px]`}
                                    >
                                        {/* Shimmer Effect Sweep on Hover / Touch */}
                                        <div
                                            className={`absolute inset-0 bg-gradient-to-r ${link.shimmerColor} opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity duration-300 pointer-events-none`}
                                        />

                                        {/* Subtle Active Accent Line */}
                                        <div
                                            className={`absolute ${isRTL ? "right-0" : "left-0"} top-0 bottom-0 w-1 sm:w-1.5 ${link.accentBg} opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity duration-200`}
                                        />

                                        {/* Left Side: Icon & Titles */}
                                        <div className="flex items-center gap-3 sm:gap-3.5 min-w-0 z-10">
                                            {/* Icon */}
                                            <div
                                                className={`p-2.5 sm:p-2.5 rounded-xl transition-all duration-200 shrink-0 shadow-2xs ${link.iconBg}`}
                                            >
                                                {link.icon}
                                            </div>

                                            {/* Text Block */}
                                            <div className="flex flex-col text-start min-w-0">
                                                <span className="font-bold text-slate-800 text-xs sm:text-sm group-hover:text-blue-700 transition-colors truncate">
                                                    {link.title}
                                                </span>
                                                <p className="text-[11px] sm:text-xs text-slate-500 truncate mt-0.5 group-hover:text-slate-600">
                                                    {link.subtitle}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Right Side: Arrow */}
                                        <div className="shrink-0 flex items-center justify-center text-slate-400 group-hover:text-blue-600 group-active:text-blue-600 transition-colors me-1 z-10">
                                            <motion.div
                                                animate={isHovered ? { x: isRTL ? -3 : 3 } : { x: 0 }}
                                                transition={{ type: "spring", stiffness: 400, damping: 20 }}
                                            >
                                                {link.internal ? (
                                                    isRTL ? <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" /> : <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                                                ) : (
                                                    <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5" />
                                                )}
                                            </motion.div>
                                        </div>
                                    </motion.div>
                                );

                                return (
                                    <motion.div key={link.id} variants={itemVariants}>
                                        {link.internal ? (
                                            <Link to={link.url} className="block w-full">
                                                {CardContent}
                                            </Link>
                                        ) : (
                                            <a href={link.url} target="_blank" rel="noopener noreferrer" className="block w-full">
                                                {CardContent}
                                            </a>
                                        )}
                                    </motion.div>
                                );
                            })}
                        </motion.div>

                        {/* Media Highlights Section */}
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.45, delay: 0.3 }}
                            className="mt-5 pt-4 border-t border-slate-200/60"
                        >
                            <div className="flex items-center justify-between mb-2.5 px-1">
                                <h2 className="text-xs sm:text-sm font-bold text-slate-700 flex items-center gap-1.5">
                                    <Sparkles className="w-3.5 h-3.5 text-blue-600" />
                                    <span>{t.mediaTitle}</span>
                                </h2>
                            </div>

                            <div className="space-y-2">
                                {mediaHighlights.map((item) => (
                                    <motion.a
                                        key={item.id}
                                        href={item.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        whileHover={{ y: -2, scale: 1.01 }}
                                        whileTap={{ scale: 0.97, y: 0 }}
                                        transition={{ type: "spring", stiffness: 400, damping: 25 }}
                                        className="group block p-2.5 sm:p-3 rounded-xl bg-white/75 hover:bg-white/95 active:bg-blue-50/70 backdrop-blur-md border border-white/80 hover:border-blue-300 shadow-2xs hover:shadow-xs transition-all duration-200"
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-2.5 min-w-0">
                                                <div className="p-1.5 sm:p-2 rounded-lg bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors shrink-0">
                                                    {item.icon}
                                                </div>
                                                <div className="min-w-0 text-start">
                                                    <h3 className="font-bold text-xs text-slate-800 group-hover:text-blue-700 transition-colors truncate">
                                                        {item.title}
                                                    </h3>
                                                    <p className="text-[10px] sm:text-[11px] text-slate-500 truncate mt-0.5">
                                                        {item.date}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="shrink-0 text-slate-400 group-hover:text-blue-600 group-active:text-blue-600 text-[11px] font-semibold flex items-center gap-1 px-2 py-1 rounded-md group-hover:bg-blue-50 transition-all">
                                                <span className="hidden sm:inline">{t.viewButton}</span>
                                                <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                                            </div>
                                        </div>
                                    </motion.a>
                                ))}
                            </div>
                        </motion.div>

                        {/* Footer in BioLink */}
                        <div className="mt-5 text-center text-slate-400 text-[11px] flex items-center justify-center gap-1">
                            <span>© {new Date().getFullYear()} Delband Health Technology</span>
                        </div>
                    </div>
                </motion.div>
            </main>
        </div>
    );
};

export default BioLinkPage;
